import { useCallback, useState } from 'react';
import {
  DEFAULT_TARGET_LANG,
  hasContent,
  isEnglish,
  resolveTranslated,
} from '../language';
import type { ScriptReviewResult, TranslatableField } from '../types';

export type ScriptTranslationState = {
  canTranslate: boolean;
  showingTranslation: boolean;
  detectedLanguage?: string;
  target: string;
  toggle: () => void;
  pick: (field: TranslatableField | null | undefined) => string;
  bodyText: string;
};

export type UseScriptTranslationInput = {
  scriptText?: string;
  data?: ScriptReviewResult;
  target?: string;
};

export function useScriptTranslation({
  scriptText,
  data,
  target = DEFAULT_TARGET_LANG,
}: UseScriptTranslationInput): ScriptTranslationState {
  const detectedLanguage = data?.detected_language;
  const bodyTranslation = data?.script_translations?.[target];
  const canTranslate = !isEnglish(detectedLanguage) && hasContent(bodyTranslation);

  const [showingTranslation, setShowingTranslation] = useState(false);

  // Force the toggle off when translation is no longer available. Guarded so it
  // can't loop; adjusted during render instead of in an effect.
  if (!canTranslate && showingTranslation) setShowingTranslation(false);

  const toggle = useCallback(() => setShowingTranslation((v) => !v), []);

  const pick = useCallback(
    (field: TranslatableField | null | undefined) =>
      resolveTranslated(field, showingTranslation, target),
    [showingTranslation, target],
  );

  const bodyText =
    showingTranslation && hasContent(bodyTranslation)
      ? bodyTranslation
      : (scriptText ?? '');

  return {
    canTranslate,
    showingTranslation,
    detectedLanguage,
    target,
    toggle,
    pick,
    bodyText,
  };
}
